import { useCallback, useEffect, useRef, useState } from "react";
import { userCacheKey } from "./telegram.ts";

interface Cached<T> {
  data: T;
  savedAt: number;
}

export interface RemoteState<T> {
  data: T | null;
  loading: boolean;
  error: unknown;
  /** Данные из кэша (нет сети) — время, когда они были получены. */
  staleSince: number | null;
  reload: () => void;
}

const INVALIDATE_EVENT = "remote:invalidate";
let invalidateTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Данные на сервере изменились (здесь, в боте или у другого члена семьи) — все открытые экраны
 * перезагружаются. Несколько сигналов подряд схлопываются в одну перезагрузку.
 */
export function invalidateRemote(): void {
  if (invalidateTimer) return;
  invalidateTimer = setTimeout(() => {
    invalidateTimer = null;
    window.dispatchEvent(new Event(INVALIDATE_EVENT));
  }, 250);
}

function readCache<T>(key: string): Cached<T> | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as Cached<T>) : null;
  } catch {
    return null;
  }
}

function writeCache<T>(key: string, data: T) {
  try {
    localStorage.setItem(key, JSON.stringify({ data, savedAt: Date.now() }));
  } catch {
    // нет места или приватный режим — работаем без кэша
  }
}

/**
 * Загрузка данных с сервера с кэшем в localStorage: офлайн показываем последние полученные данные.
 * Кэш у каждого пользователя свой.
 */
export function useRemote<T>(key: string, load: () => Promise<T>): RemoteState<T> {
  const cacheKey = `remote:${userCacheKey()}:${key}`;
  const [state, setState] = useState<Omit<RemoteState<T>, "reload">>(() => {
    const cached = readCache<T>(cacheKey);
    return { data: cached?.data ?? null, loading: true, error: null, staleSince: null };
  });
  const loadRef = useRef(load);
  loadRef.current = load;
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const cached = readCache<T>(cacheKey);
    setState({ data: cached?.data ?? null, loading: true, error: null, staleSince: null });
    loadRef.current().then(
      (data) => {
        if (cancelled) return;
        writeCache(cacheKey, data);
        setState({ data, loading: false, error: null, staleSince: null });
      },
      (error) => {
        if (cancelled) return;
        setState({ data: cached?.data ?? null, loading: false, error, staleSince: cached ? cached.savedAt : null });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [cacheKey, tick]);

  const reload = useCallback(() => setTick((n) => n + 1), []);

  // Перезагрузка по сигналу об изменениях и при возвращении в приложение (например, из чата с ботом).
  useEffect(() => {
    const onVisible = () => document.visibilityState === "visible" && reload();
    window.addEventListener(INVALIDATE_EVENT, reload);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener(INVALIDATE_EVENT, reload);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [reload]);
  return { ...state, reload };
}
